// Filtros rápidos compartidos por el tablero y la lista. Se reflejan en la URL para poder guardarlos y compartirlos.
import type { CasoResumen, Prioridad, TipoCaso } from '../api/tipos';
import { ESTADOS_CERRADOS } from './etiquetas';
import { vencido } from './formato';

export const SIN_DUENO = '__sin__';

export interface FiltrosRapidos {
  q: string;
  duenos: string[];
  tipos: TipoCaso[];
  prioridades: Prioridad[];
  etiquetas: string[];
  mios: boolean;
  vencidos: boolean;
}

export const FILTROS_VACIOS: FiltrosRapidos = { q: '', duenos: [], tipos: [], prioridades: [], etiquetas: [], mios: false, vencidos: false };

const lista = (v: string | null) => (v ? v.split(',').filter(Boolean) : []);

export function filtrosDesdeUrl(p: URLSearchParams): FiltrosRapidos {
  return {
    q: p.get('q') ?? '',
    duenos: lista(p.get('dueno')),
    tipos: lista(p.get('tipo')) as TipoCaso[],
    prioridades: lista(p.get('prioridad')) as Prioridad[],
    etiquetas: lista(p.get('etiqueta')),
    mios: p.get('mios') === '1',
    vencidos: p.get('vencidos') === '1',
  };
}

export function filtrosAUrl(f: FiltrosRapidos, base: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(base);
  const fijar = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k));
  fijar('q', f.q.trim());
  fijar('dueno', f.duenos.join(','));
  fijar('tipo', f.tipos.join(','));
  fijar('prioridad', f.prioridades.join(','));
  fijar('etiqueta', f.etiquetas.join(','));
  fijar('mios', f.mios ? '1' : '');
  fijar('vencidos', f.vencidos ? '1' : '');
  return p;
}

export function hayFiltros(f: FiltrosRapidos): boolean {
  return !!(f.q.trim() || f.duenos.length || f.tipos.length || f.prioridades.length || f.etiquetas.length || f.mios || f.vencidos);
}

export const estaVencido = (c: Pick<CasoResumen, 'fecha_limite' | 'estado'>) => vencido(c.fecha_limite) && !ESTADOS_CERRADOS.includes(c.estado);

export function aplicarFiltros(casos: CasoResumen[], f: FiltrosRapidos, miOid: string): CasoResumen[] {
  const t = f.q.trim().toLowerCase();
  return casos.filter((c) => {
    if (t && !`${c.case_id} ${c.tema} ${c.cliente} ${(c.etiquetas ?? []).join(' ')}`.toLowerCase().includes(t)) return false;
    if (f.duenos.length && !f.duenos.includes(c.dueno ?? SIN_DUENO)) return false;
    if (f.tipos.length && !f.tipos.includes(c.tipo)) return false;
    if (f.prioridades.length && !f.prioridades.includes(c.prioridad)) return false;
    if (f.etiquetas.length && !f.etiquetas.some((e) => (c.etiquetas ?? []).includes(e))) return false;
    if (f.mios && c.dueno !== miOid) return false;
    if (f.vencidos && !estaVencido(c)) return false;
    return true;
  });
}

/** Etiquetas válidas para la API: minúsculas, sin espacios ni tildes problemáticas, máximo 40 caracteres. */
export function normalizarEtiqueta(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-_.]/g, '')
    .slice(0, 40);
}
